import React from 'react';
import { Mail, Phone, Linkedin } from 'lucide-react';
import founderPortrait from '@/assets/founder-portrait.jpg';
import SEO from '../components/SEO';

const FounderProfile = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Founder Profile — Omollo Victor | Law and Code Africa`}
        description={`Omollo Victor, LLB Hons, CPM, Trainee Advocate. Founder of Law and Code Africa and Wakili Assistant — legal-tech, AI law, and ADR.`}
        path="/founder-profile"
      />
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-primary">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Omollo Victor
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              LLB Hons | Certified Professional Mediator | Trainee Advocate | Low-Code Developer | Student Member CIArb | Certified Secretary (Advanced)
            </p>
            <div className="max-w-6xl mx-auto text-lg text-blue-100 space-y-4 text-justify px-4">
              <p>
                Omollo Victor is a dedicated and innovative legal professional with strong expertise in Alternative Dispute Resolution, Commercial Law, Intellectual Property, Cyber Law, AI Law, and Corporate Governance. Fully in touch with emerging issues, he is committed to advancing modern legal practice through thoughtful, future-focused solutions.
              </p>
              <p>
                Beyond his legal work, Victor is also a low-code developer with experience in UI and UX design. As the Founder of Wakili Assistant, he leverages technology to build impactful legal-tech tools and automation systems that enhance efficiency and access to legal services.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        {/* Professional Image and Contact */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center">
              <div className="relative overflow-hidden rounded-lg shadow-lg w-full max-w-sm mb-6">
                <img 
                  src={founderPortrait}
                  alt="Omollo Victor - Professional Profile"
                  className="w-full h-auto object-cover transition-all duration-700 hover:scale-110 hover:brightness-110"
                />
              </div>
              <div className="bg-gray-50 p-6 rounded-lg w-full max-w-sm">
                <h3 className="text-xl font-bold text-primary mb-4 text-center">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="text-secondary" size={20} />
                    <span className="text-gray-700">lawandcodeafrica@gmail.com</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="text-secondary" size={20} />
                    <span className="text-gray-700">+254731797354</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Linkedin className="text-secondary" size={20} />
                    <a href="https://www.linkedin.com/in/victor-omollo065" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Connect on LinkedIn</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-primary mb-8">Background and Qualifications</h2>
            <div className="space-y-6 text-gray-700">
              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Educational Foundation</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Kanga National School:</strong> Attended Kanga National School, where he held the position of Secretary General of the Student Governing Council, demonstrating early leadership capabilities.
                  </p>
                  <p>
                    <strong>Kisii University (LLB):</strong> Obtained an LLB from Kisii University. During his tenure, contributed to legal and governance frameworks as a member of the Kisii University Students Association (KSUSA) Constitution Review Committee.
                  </p>
                  <p>
                    <strong>Kenya School of Law:</strong> Currently an ATP Student at the Kenya School of Law.
                  </p>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Qualifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Certified Professional Mediator</h4>
                    <p className="text-sm text-gray-600">Holds certification as a Professional Mediator</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Trainee Advocate</h4>
                    <p className="text-sm text-gray-600">Currently undergoing training as an Advocate</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Student Member CIArb</h4>
                    <p className="text-sm text-gray-600">Student member of the Chartered Institute of Arbitrators</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Low-Code/No-Code Developer</h4>
                    <p className="text-sm text-gray-600">Proficient in low-code/no-code development</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Certified Secretary</h4>
                    <p className="text-sm text-gray-600">Certified Secretary, Advanced Level</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Experience & Community Involvement</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Ogwe and Associates Advocates:</strong> Completed internship gaining practical legal experience in various practice areas.
                  </p>
                  <p>
                    <strong>Baraka CBO:</strong> Founding member of Baraka Community Based Organization based in Kisii, offering "comrade support" to deserving students.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Areas of Specialization */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center">
            Key Practice Areas and Interests
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10 max-w-4xl mx-auto">
            Omollo Victor's professional interests and developing specializations encompass:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Alternative Dispute Resolution (ADR)</h3>
              <p className="text-gray-600">
                Focus on mediation, arbitration, and non-litigious dispute resolution mechanisms across various sectors including technology and commercial disputes.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Commercial Law</h3>
              <p className="text-gray-600">
                Corporate governance frameworks, commercial contracts negotiation and drafting, international trade law compliance, and business regulatory matters.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Intellectual Property (IP) Law</h3>
              <p className="text-gray-600">
                Protection of innovations, patent applications, trademark registration, copyright matters, and IP licensing in technology sectors.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Cyber Law</h3>
              <p className="text-gray-600">
                Data protection compliance, cybersecurity regulations, digital privacy rights, and legal frameworks for digital transactions.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">AI Law</h3>
              <p className="text-gray-600">
                AI governance frameworks, algorithmic accountability, machine learning compliance, and regulatory implications of artificial intelligence deployment.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FounderProfile;
